import { AuthErrorKeys, useAuthService } from '@b2ag/auth'
import { setCurrency } from '@b2ag/locale'
import { removeMicroFrontListener, getAppContext } from '@b2ag/micro-front-tools'
import { routingCancelCheckout } from '@b2ag/checkout'
import { locationStorage } from '../services'
import { initAxeptioIfNeeded } from '../helpers/axeptio.helper'
import { checkAndRedirectIfUserIsNotOnCorrectLocalizedRoute } from '@b2ag/utils/src/i18n-routing'
import { useCurrentMembership } from '@b2ag/membership/src/composables/useCurrentMembership'

const { isFrontAgri, isFrontTech } = getAppContext()

export async function routerBeforeEach({ storeInstance }, to, from, next) {
  if (import.meta.browser && !window.location.href.includes('oauth')) {
    checkAndRedirectIfUserIsNotOnCorrectLocalizedRoute(storeInstance.getters.user?.currentProfile?.cooperative?.locales)
  }

  if (import.meta.browser && to.path !== from.path) removeMicroFrontListener(window.location.origin)

  if (to.meta.disabled) {
    return next({ name: 'not-found' })
  }

  if (!to.meta.doNotStoreRoute || to.meta.storePathBeforeAuth || to.query?.forceLogin) {
    let { path } = to

    if (to.name === 'search') path = to.fullPath.replace('&forceLogin=true', '').replace('?forceLogin=true', '')

    locationStorage.storeLocation(path)
  }

  /* Dernière vérification avant d'entrée dans un état possiblement connecté : to.meta.oauthWorkflow */

  if (to.meta.oauthWorkflow) {
    return next()
  }

  const authService = useAuthService()
  let isAuthenticated = await authService.isAuthenticated()

  if (!isAuthenticated && (to.meta.private || to.query?.forceLogin || isFrontTech)) {
    try {
      await authService.checkUserAuthSession()
      isAuthenticated = await authService.isAuthenticated()
    } catch (error: any) {
      if (error.message === AuthErrorKeys.IS_NOT_AUTHENTICATED) {
        await authService.login()
        return false
      }
      if (error.message === AuthErrorKeys.INVALID_SCOPE || error.message.includes('not-allowed')) {
        return next({ name: 'auth-callback-error', query: { error_code: error.message } })
      }
    }
  }

  /* À partir d'ici, on entre forcément dans un environnement connecté */

  if (isAuthenticated) {
    if (
      (isFrontAgri && !storeInstance.getters.isStoreInitialized) ||
      (isFrontTech && !storeInstance.getters.isUserLoaded)
    ) {
      await storeInstance.dispatch('fetchUser')
      if (isFrontAgri) storeInstance.commit('SET_IS_STORE_INITIALIZED', true)

      setCurrency(storeInstance.getters.user?.cooperatives?.[0]?.currency)

      // We 'install' / initialize the axeptio SDK here because we need to set its consent token before its initialization
      initAxeptioIfNeeded(storeInstance.getters.user?.consent_token)
    }

    if (
      isFrontAgri &&
      !to.meta.inscriptionFlow &&
      !storeInstance.getters.hasConfirmedProfile &&
      to.name !== 'account-add-cooperative'
    ) {
      next({ name: 'account-add-cooperative' })
      return true
    }

    // CGU verification
    if (isFrontAgri && !storeInstance.getters.isCGUFarmerAccepted && to.name !== 'accept-cgu-agri') {
      return next({ name: 'accept-cgu-agri' })
    }
    if (isFrontTech && !storeInstance.getters.isCGUAccepted && to.name !== 'accept-cgu-tech') {
      return next({ name: 'accept-cgu-tech' })
    }

    try {
      if (from.name?.includes('buy-layout') && !to.name?.includes('buy-layout') && from.hash !== '#confirmation') {
        if (isFrontTech) await useCurrentMembership().loadCurrentMembership()
        await routingCancelCheckout(from.params.checkoutId)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  } else if (to.name !== 'auth-callback' && from.name !== 'auth-callback') {
    initAxeptioIfNeeded(storeInstance.getters.user?.consent_token)
  }

  if (from.href === to.href || from.href === to.fullPath) {
    // XJU : custom fix to prevent redirection in SPA mode after initial load
    return next(false)
  }

  return next()
}
